import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { CheckCircle2, FilePlus2, Loader2, Trash2, UploadCloud, XCircle } from "lucide-react";

const UNASSIGNED = "__unassigned__";

/**
 * Bulk attach finished reports: pick many files at once, map each file to an
 * order (auto-matched when the filename contains the order id prefix), then
 * attach all in a single request. Each member still gets their normal
 * email + notification per order.
 */
export const BulkAttachDialog = ({ open, onOpenChange, orders, onDone }) => {
  const [entries, setEntries] = useState([]); // { file, orderId, status: idle|success|error, error }
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Orders that still need a finished report first, then everything else.
  const orderOptions = useMemo(() => {
    const rows = [...(orders || [])];
    rows.sort((a, b) => {
      const aNeeds = !a.report_filename && a.admin_status !== "cancelled" ? 0 : 1;
      const bNeeds = !b.report_filename && b.admin_status !== "cancelled" ? 0 : 1;
      return aNeeds - bNeeds;
    });
    return rows;
  }, [orders]);

  const autoMatch = (fileName, taken) => {
    const lower = (fileName || "").toLowerCase();
    const match = orderOptions.find(
      (order) => !taken.has(order.id) && lower.includes(order.id.slice(0, 8).toLowerCase())
    );
    return match ? match.id : "";
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setEntries((previous) => {
      const taken = new Set(previous.map((entry) => entry.orderId).filter(Boolean));
      const additions = files
        .filter((file) => !previous.some((entry) => entry.file.name === file.name && entry.file.size === file.size))
        .map((file) => {
          const matched = autoMatch(file.name, taken);
          if (matched) taken.add(matched);
          return { file, orderId: matched, status: "idle", error: "" };
        });
      return [...previous, ...additions].slice(0, 25);
    });
  };

  const setEntryOrder = (index, orderId) => {
    setEntries((previous) =>
      previous.map((entry, i) =>
        i === index ? { ...entry, orderId: orderId === UNASSIGNED ? "" : orderId, status: "idle", error: "" } : entry
      )
    );
  };

  const removeEntry = (index) => {
    setEntries((previous) => previous.filter((_, i) => i !== index));
  };

  const reset = () => {
    setEntries([]);
    setSubmitting(false);
  };

  const duplicateOrderIds = useMemo(() => {
    const counts = {};
    entries.forEach((entry) => {
      if (entry.orderId) counts[entry.orderId] = (counts[entry.orderId] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((id) => counts[id] > 1));
  }, [entries]);

  const unassignedCount = entries.filter((entry) => !entry.orderId).length;
  const canSubmit =
    entries.length > 0 && unassignedCount === 0 && duplicateOrderIds.size === 0 && !submitting;

  const attachAll = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const body = new FormData();
    entries.forEach((entry) => body.append("reports", entry.file));
    body.append("assignments", JSON.stringify(entries.map((entry) => entry.orderId)));
    try {
      const { data } = await api.post("/admin/orders/bulk-report", body);
      const results = data.results || [];
      setEntries((previous) =>
        previous.map((entry, index) => {
          const result = results[index] || {};
          return {
            ...entry,
            status: result.success ? "success" : "error",
            error: result.error || "",
          };
        })
      );
      if (data.attached === data.total) {
        toast.success(`All ${data.attached} reports attached. Members have been notified and emailed.`);
        onDone?.();
        setTimeout(() => {
          onOpenChange(false);
          reset();
        }, 1200);
      } else {
        toast.warning(`${data.attached} of ${data.total} reports attached. Review the failures below.`, {
          duration: 8000,
        });
        onDone?.();
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Bulk attach failed."), { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  const orderLabel = (order) =>
    `${order.id.slice(0, 8)} — ${order.user_email || "unknown"}${order.report_filename ? " (has report)" : ""}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-2xl" data-testid="bulk-attach-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk attach finished reports</DialogTitle>
          <DialogDescription>
            Select several finished report files, map each one to its order, and attach them all at once.
            Files whose names contain the 8-character order ID are matched automatically. Each member
            receives their normal email with the report attached.
          </DialogDescription>
        </DialogHeader>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.png,.jpg,.jpeg"
          className="hidden"
          data-testid="bulk-attach-file-input"
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
        />

        <button
          type="button"
          data-testid="bulk-attach-dropzone"
          className="w-full rounded-lg border border-dashed border-border bg-secondary/40 px-4 py-6 text-center transition-colors hover:border-primary hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            addFiles(event.dataTransfer.files);
          }}
        >
          <UploadCloud className="h-6 w-6 text-primary mx-auto mb-1.5" />
          <p className="text-sm font-medium">Click to choose files or drag &amp; drop</p>
          <p className="text-xs text-muted-foreground mt-0.5">PDF, DOCX, PNG, JPG — up to 25 files, 10 MB each</p>
        </button>

        {entries.length > 0 && (
          <ScrollArea className="max-h-64 pr-2">
            <div className="space-y-2" data-testid="bulk-attach-file-list">
              {entries.map((entry, index) => (
                <div
                  key={`${entry.file.name}-${index}`}
                  className="flex items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2"
                  data-testid={`bulk-attach-row-${index}`}
                >
                  <FilePlus2 className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm" title={entry.file.name}>
                      {entry.file.name}
                    </p>
                    {entry.status === "error" && (
                      <p className="text-xs text-destructive" data-testid={`bulk-attach-error-${index}`}>
                        {entry.error || "Failed to attach."}
                      </p>
                    )}
                  </div>
                  {entry.status === "success" ? (
                    <span
                      className="flex items-center gap-1 text-xs text-[hsl(var(--status-verified))]"
                      data-testid={`bulk-attach-success-${index}`}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Attached
                    </span>
                  ) : (
                    <>
                      <Select
                        value={entry.orderId || UNASSIGNED}
                        onValueChange={(value) => setEntryOrder(index, value)}
                      >
                        <SelectTrigger
                          className={`h-8 w-56 ${
                            !entry.orderId || duplicateOrderIds.has(entry.orderId)
                              ? "border-[hsl(var(--status-mismatch))]"
                              : ""
                          }`}
                          data-testid={`bulk-attach-order-select-${index}`}
                        >
                          <SelectValue placeholder="Assign order" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED}>— Assign order —</SelectItem>
                          {orderOptions.map((order) => (
                            <SelectItem key={order.id} value={order.id}>
                              {orderLabel(order)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Remove file"
                        data-testid={`bulk-attach-remove-${index}`}
                        onClick={() => removeEntry(index)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        {duplicateOrderIds.size > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-destructive" data-testid="bulk-attach-duplicate-warning">
            <XCircle className="h-3.5 w-3.5" /> The same order is assigned to more than one file.
          </p>
        )}
        {entries.length > 0 && unassignedCount > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="bulk-attach-unassigned-note">
            {unassignedCount} file{unassignedCount === 1 ? "" : "s"} still need{unassignedCount === 1 ? "s" : ""} an
            order assignment.
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="bulk-attach-cancel-button"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Close
          </Button>
          <Button data-testid="bulk-attach-submit-button" disabled={!canSubmit} onClick={attachAll}>
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <UploadCloud className="h-4 w-4 mr-1.5" />
            )}
            Attach all ({entries.filter((entry) => entry.status !== "success").length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
